import { useId, useState, type FormEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { cn } from '../../shared/lib/cn';
import { shortDate } from '../../shared/lib/format';
import { ApiError } from '../../shared/api/errors';
import { Button } from '../../shared/ui/button';
import { ConfirmDelete } from '../../shared/ui/confirm-delete';
import { CopyField } from '../../shared/ui/copy-field';
import { FieldLabel } from '../../shared/ui/field-label';
import { Hint } from '../../shared/ui/hint';
import { Input } from '../../shared/ui/input';
import { Banner } from '../../shared/ui/layout';
import { Modal } from '../../shared/ui/modal';
import { Note } from '../../shared/ui/note';
import { Segmented } from '../../shared/ui/segmented';
import {
  addSourceSecret,
  generalAddressRejected,
  generalNeverArrived,
  generalRejectionLine,
  overlapLine,
  revokeSourceSecret,
  rotateSourceSecret,
  secretOverlap,
  GRACE_HOURS,
  type TGraceHours,
  type TSourceSecret,
  type TSourceVerification,
} from '../../entities/source';

const GRACE_SEGMENTS = GRACE_HOURS.map((hours) => ({
  value: String(hours),
  label: hours === 1 ? '1 hour' : `${hours} hours`,
}));

const emptyLine = (checked: boolean): string =>
  checked
    ? 'This source is proved by where a request comes from, which takes no secret.'
    : 'A secret belongs to a check, and there is none to attach it to yet.';

const waitingLine = (neverArrived: boolean): string =>
  neverArrived
    ? 'Until a secret is here, the general address rejects what it receives. Nothing has arrived on it yet.'
    : 'Until a secret is here, the general address rejects what it receives.';

const prefixLine = (prefix: string, senderName: string | null, stored: boolean): string =>
  `${stored ? 'The stored secret does not' : 'That does not'} begin with "${prefix}", the marker ` +
  `${senderName ?? 'the sender'} prints on every signing secret — store it exactly as the ` +
  'dashboard shows it, marker and all.';

interface ISourceSecretsProps {
  sourceId: string;
  methodId: string | null;
  checked: boolean;
  senderName: string | null;
  secretPrefix: string | null;
  secrets: TSourceSecret[];
  verification: TSourceVerification | undefined;
  generalAddressOpen: boolean;
  onCloseGeneralAddress: () => void;
  closingGeneralAddress?: boolean;
  timeZone?: string;
  now: number;
  className?: string;
}

export const SourceSecrets = ({
  className,
  sourceId,
  methodId,
  checked,
  senderName,
  secretPrefix,
  secrets,
  verification,
  generalAddressOpen,
  onCloseGeneralAddress,
  closingGeneralAddress,
  timeZone,
  now,
}: ISourceSecretsProps) => {
  const client = useQueryClient();
  const fieldId = useId();
  const [replacing, setReplacing] = useState(false);
  const [revoking, setRevoking] = useState<TSourceSecret | null>(null);
  const [value, setValue] = useState('');
  const [grace, setGrace] = useState<TGraceHours>(24);
  const [created, setCreated] = useState<string | null>(null);

  const refresh = () => client.invalidateQueries({ queryKey: ['source-secrets', sourceId] });

  const close = () => {
    setReplacing(false);
    setRevoking(null);
    setValue('');
  };

  const add = useMutation({
    mutationFn: () => addSourceSecret(sourceId, methodId ?? '', value.trim() || undefined),
    onSuccess: (secret) => {
      const pasted = value.trim().length > 0;
      close();
      if (!pasted) setCreated(secret.plaintext);
      void refresh();
    },
  });

  const rotate = useMutation({
    mutationFn: () =>
      rotateSourceSecret(sourceId, methodId ?? '', {
        graceHours: grace,
        value: value.trim() || undefined,
      }),
    onSuccess: (result) => {
      const pasted = value.trim().length > 0;
      close();
      if (!pasted) setCreated(result.active.plaintext);
      void refresh();
    },
  });

  const revoke = useMutation({
    mutationFn: (secret: TSourceSecret) => revokeSourceSecret(sourceId, secret.id),
    onSuccess: () => {
      close();
      void refresh();
    },
  });

  const live = secrets.find((secret) => secret.state === 'active') ?? null;
  const overlap = secretOverlap(secrets);
  const older = secrets.find((secret) => secret.state === 'grace') ?? null;

  const shared = (verification?.clientAddressCount ?? 0) > 0;
  const label = shared
    ? 'Secret for the general address'
    : senderName
      ? `Secret from the ${senderName} dashboard`
      : 'Secret from the sender’s dashboard';

  const typed = value.trim();
  const mistyped = secretPrefix !== null && typed.length > 0 && !typed.startsWith(secretPrefix);
  const storedWrong =
    live !== null && secretPrefix !== null && !live.secretHint.startsWith(secretPrefix);

  // Generating only means something where we issue the secret; a named vendor's comes from their dashboard.
  const canGenerate = senderName === null;

  const rejection = (error: unknown): ApiError | null => (error instanceof ApiError ? error : null);

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (!mistyped && (typed.length > 0 || canGenerate)) add.mutate();
  };

  if (methodId === null) {
    return <p className={cn('text-meta text-muted', className)}>{emptyLine(checked)}</p>;
  }

  // A closed general address answers 404 before verifying, so an empty field here is not a gap.
  if (!generalAddressOpen && !live) return null;

  const rejected = generalAddressRejected(verification);

  return (
    <div className={className}>
      <FieldLabel htmlFor={fieldId}>{label}</FieldLabel>

      {live ? (
        <div className="flex items-center gap-2">
          <Input
            mono
            disabled
            id={fieldId}
            className="min-w-0 flex-1"
            value={`${live.secretHint} · set ${shortDate(live.createdAt, timeZone)}`}
          />
          <Button
            variant="ghost"
            className="shrink-0 bg-ghost text-primary"
            onClick={() => setReplacing(true)}
          >
            Replace
          </Button>
        </div>
      ) : (
        <form className="flex items-center gap-2" onSubmit={submit}>
          <Input
            mono
            id={fieldId}
            className="min-w-0 flex-1"
            placeholder={secretPrefix ? `${secretPrefix}…` : 'Paste the signing secret'}
            invalid={mistyped}
            value={value}
            onChange={(event) => setValue(event.target.value)}
          />
          <Button
            type="submit"
            className="shrink-0"
            disabled={mistyped || (typed.length === 0 && !canGenerate)}
            waiting={add.isPending}
            waitingLabel="Saving"
          >
            {typed.length === 0 && canGenerate ? 'Generate one' : 'Save'}
          </Button>
        </form>
      )}

      {mistyped && secretPrefix && (
        <Note tone="danger" className="mt-2">
          {prefixLine(secretPrefix, senderName, false)}
        </Note>
      )}

      {storedWrong && secretPrefix && (
        <Note tone="danger" className="mt-2">
          {prefixLine(secretPrefix, senderName, true)}
        </Note>
      )}

      {!live && !mistyped && rejected > 0 && verification && (
        <Note tone="danger" className="mt-2">
          <p className="text-data text-primary">{generalRejectionLine(verification)}</p>
          <p className="mt-2 text-meta leading-18 text-muted">
            Either add one, or{' '}
            <button
              type="button"
              className="text-primary underline"
              disabled={closingGeneralAddress}
              onClick={onCloseGeneralAddress}
            >
              close the general address
            </button>{' '}
            if this sender only uses client addresses.
          </p>
        </Note>
      )}

      {!live && !mistyped && rejected === 0 && (
        <div className="mt-2 flex gap-inset rounded-control bg-surface-sunken px-input py-3">
          <span aria-hidden className="mt-label size-dot shrink-0 rounded-full bg-motion" />
          <span className="text-meta leading-18 text-muted">
            {waitingLine(generalNeverArrived(verification))}
          </span>
        </div>
      )}

      {!live && !mistyped && canGenerate && (
        <Hint>
          Leave it empty and hookpipe generates one instead — then paste that into whatever signs
          the requests.
        </Hint>
      )}

      {add.isError && (
        <div className="mt-3">
          <Banner detail={rejection(add.error)?.code}>
            {rejection(add.error)?.message ?? 'That was not accepted. Nothing was saved.'}
          </Banner>
        </div>
      )}

      {overlap && (
        <div className="mt-3 flex items-center gap-inset rounded-option bg-surface-sunken px-input py-3">
          <span aria-hidden className="size-dot shrink-0 rounded-full bg-motion" />
          <span className="min-w-0 flex-1 text-meta leading-18 text-muted">
            {overlapLine(overlap, now, timeZone)}
          </span>
          {older && (
            <Button
              variant="ghost"
              size="sm"
              className="h-control-xs shrink-0"
              onClick={() => setRevoking(older)}
            >
              Revoke
            </Button>
          )}
        </div>
      )}

      <Modal
        open={replacing}
        onOpenChange={(next) => !next && close()}
        title={senderName ? `Replace the ${senderName} secret` : 'Replace the signing secret'}
        description="The new secret is live at once. The one it replaces keeps verifying for the window you pick, so a sender you have not switched over yet is not cut off."
        submit={{
          label: 'Replace',
          onSubmit: () => rotate.mutate(),
          disabled: mistyped || (typed.length === 0 && !canGenerate),
          waiting: rotate.isPending,
          waitingLabel: 'Replacing',
        }}
      >
        <Input
          mono
          autoFocus
          invalid={mistyped}
          placeholder={secretPrefix ? `${secretPrefix}…` : 'Paste the new signing secret'}
          value={value}
          onChange={(event) => setValue(event.target.value)}
        />
        {mistyped && secretPrefix && (
          <Note tone="danger" className="mt-3">
            {prefixLine(secretPrefix, senderName, false)}
          </Note>
        )}
        <p className="mt-4 mb-label text-micro font-medium tracking-group text-faint">
          The old one keeps verifying for
        </p>
        <Segmented
          aria-label="Grace window"
          segments={GRACE_SEGMENTS}
          value={String(grace)}
          onChange={(next) => setGrace(Number(next) as TGraceHours)}
        />
        {rotate.isError && (
          <div className="mt-3">
            <Banner detail={rejection(rotate.error)?.code}>
              {rejection(rotate.error)?.message ?? 'That was not accepted. Nothing was saved.'}
            </Banner>
          </div>
        )}
      </Modal>

      <ConfirmDelete
        open={revoking !== null}
        onOpenChange={(next) => !next && close()}
        entity="secret"
        verb="Revoke"
        name={revoking?.secretHint ?? ''}
        note="Any sender still signing with this one is answered as if it had never been configured."
        waiting={revoke.isPending}
        onConfirm={() => revoking && revoke.mutate(revoking)}
      />

      <Modal
        open={created !== null}
        onOpenChange={(next) => !next && setCreated(null)}
        title="Copy it now"
        description="This is the only time the value is shown. hookpipe keeps a hash, so it cannot be read back."
        footer={
          <Button size="sm" onClick={() => setCreated(null)}>
            I have it
          </Button>
        }
      >
        <CopyField value={created ?? ''} />
      </Modal>
    </div>
  );
};
